import classNames from "classnames"
import { useRouter } from "next/router"
import React from "react"

import type { GeocodingResult } from "@bounce/google-place"
import { useTranslation } from "@bounce/i18n"
import { Button } from "@bounce/web-components"

import { AddressField } from "../../../../components/Generic/AddressField/AddressField"
import { InteractionEvents } from "../../../../libs/analytics/events"
import { trackSearchBar } from "../../../../utils/analytics"

import SearchIcon from "@bounce/assets/icons/Search.svg"

export type SearchBarProps = {
  className?: string
  initialValue?: string
  value?: string
  onSearch?: (geolocation: GeocodingResult | string | undefined) => void
  onChange?: (value: string) => void
}

type CTAButtonProps = {
  onClick: () => void
}

const CTAButton = ({ onClick }: CTAButtonProps) => {
  const { t } = useTranslation()

  return (
    <Button
      className="mx-auto mt-4 h-12 w-full md:mt-0 md:h-16 md:w-auto lg:mx-0"
      textClassName="line-clamp-1"
      iconClassName="size-5"
      color="primary"
      onClick={onClick}>
      {t("screen.cityListView.searchBar.title", "Search")}
    </Button>
  )
}

export const SearchBar = ({
  className,
  initialValue,
  value,
  onSearch = () => {},
  onChange = () => {},
}: SearchBarProps) => {
  const { t } = useTranslation()
  const router = useRouter()

  // todo: we need tracking for this
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onSearch(value)
  }

  const handleSearch = async () => onSearch(value)

  const handleSelectRecommendation = (recommendation: GeocodingResult | undefined) => {
    if (recommendation) {
      trackSearchBar({
        event: InteractionEvents.LuggageStorageSearchBarLocationSuggestionPressed,
        props: {
          from: "CityListView",
          pageName: router.route,
          pagePath: router.asPath,
          searchQuery: recommendation.query,
          searchQueryType: recommendation.scope,
          searchedCountry: recommendation.country,
          searchedCity: recommendation.city,
          searchedType: recommendation.scope,
          searchedSubtype: recommendation.type,
        },
      })
    }

    onSearch(recommendation)
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={classNames("flex w-full flex-col items-center px-6 md:flex-row lg:px-0", className)}>
      <div className="relative flex w-full rounded-full border border-neutral-300 bg-white text-neutral-800 md:mr-4">
        <AddressField
          className="flex-shrink-0 flex-grow pl-2 md:pl-4"
          defaultValue={initialValue ?? ""}
          value={value}
          leftIcon={<SearchIcon />}
          onChange={onChange}
          onSelectRecommendation={handleSelectRecommendation}
          placeholder={t("cmp.searchBar.placeholder", "Search place or address")}
        />
      </div>
      <CTAButton onClick={handleSearch} />
    </form>
  )
}
